import { ChangeDetectorRef, Component, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateUserFilterTask, Filter, FilterCondition, IssueCategory, TaskType, Widget, WidgetUserFilter } from 'src/app/classes';
import { DataService, TaskResponse } from 'src/app/services/data.service';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { BackEndService } from 'src/app/backend-service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Guid } from 'src/app/globals';
import { WidgetTypeMap } from '../../widget/widget-globals';
import { IssueService } from 'src/app/lib/issue/services/issue.service';
import { map } from 'rxjs/operators';
import { IssuePriority, IssueStatus } from 'src/app/lib/issue/classes';
import { ContractService } from 'src/app/lib/contract/services/contract.service';
import { ContractStatus, ContractType } from 'src/app/lib/contract/classes';
import { UserService } from 'src/app/lib/user/services/user.service';
import { User } from 'src/app/lib/user/classes';

export const StaticFilterTypeCriteriaProviders = {
  "Priority": (criteria, injector: Injector) => injector.get(IssueService).getAllIssuePriorities(true).pipe(map((priorities: IssuePriority[]) => {
    return priorities.map(x => {
      return { criteria: criteria, value: x.priorityid, title: x.priorityname } as CriteriaSuggestion<any>;
    });
  })),
  "ContractState": (criteria, injector: Injector) => injector.get(ContractService).loadAllContractStatus(true).pipe(map((status: ContractStatus[]) => {
    return status.map(x => {
      return { criteria: criteria, value: x.contractstateid, title: x.contractstatename } as CriteriaSuggestion<any>;
    });
  })),
  "ContractType": (criteria, injector: Injector) => injector.get(ContractService).loadAllContractTypes(true).pipe(map((types: ContractType[]) => {
    return types.map(x => {
      return { criteria: criteria, value: x.contracttypeid, title: x.contracttypename } as CriteriaSuggestion<any>;
    });
  }))
}

export const UserCriteriaProvider = (criteria, injector: Injector) => {
  return injector.get<UserService>(UserService).loadAllAMSUsers().pipe(map((users: User[]) => {
    return users.map(x => {
      return { criteria: criteria, value: x.id, title: x.userName } as CriteriaSuggestion<any>;
    });
  }));
};

export const StaticFilterTpeCriteria = {
  "IssueUserConnectionAssigned": {
    value: "isAssigned", title: "Eingeteilt", suggestionsProvider: UserCriteriaProvider
  },
  "IssueFilesArchived": {
    value: "filesexported", title: "Dateien archiviert", suggestionsProvider: (criteria, injector) => {
      return of([
        { value: "true", criteria: criteria, title: "Dateien archiviert" } as CriteriaSuggestion<string>,
        { value: "false", criteria: criteria, title: "Dateien nicht archiviert" } as CriteriaSuggestion<string>
      ]);
    }
  }
}

@Component({
  selector: 'app-edit-userfilter',
  templateUrl: './edit-userfilter.component.html',
  styleUrls: ['./edit-userfilter.component.css']
})
export class EditUserfilterComponent implements OnDestroy, OnInit {

  formSettedUp: boolean = false;
  valuesInitiallySetted: boolean = false;
  formIsValid: boolean = false;
  formHasChanges: boolean = false;

  public sortDirection: string = "aufsteigend";

  private _userFilter: WidgetUserFilter;
  @Input() set userFilter(filter: WidgetUserFilter) {
    this._userFilter = filter;
    this.savedFilter = filter.filteR1;
    if (this.formSettedUp && !this.valuesInitiallySetted) this.setFormValues();
    let widget = this._dataService.startPage.find(x => x.widgetid === this.userFilter.filterwidget);
    if (widget) {
      let type = this._dataService.widgetTypes.find(x => x.wtypeid === widget.widgettype);
      let internal = WidgetTypeMap[type.wtypename];
      if (!internal) return;
      this.widgetTypeName = internal.pretty;
    }
  }
  get userFilter(): WidgetUserFilter {
    return this._userFilter;
  }

  @Input() inDialog: boolean = true;
  @Input() saveFinished: Subject<WidgetUserFilter>;
  @Input() closeAnchor: Subject<void>;

  public formGroup: FormGroup;

  public statementValid: boolean = true;
  public corruptedConstantInStatement: boolean = false;
  public compiledStatement: Term[];
  public compiledFilters: Filter[] = [];
  public savedFilter: Filter;

  canModifyFilterConditions: boolean = false;
  currentFilterType: FilterType<number>;
  currentSuggestions = [{ title: "Aktiv", value: 1 }];

  subscriptions: Subscription[] = [];
  rightsMap = {
    "isAdmin": false
  };

  widgetTypeName: string = "unknown type";

  currentUserWidgetMap: UserWidgetMap[];
  currentUserWidgetMapLoading: boolean = false;
  currentUserWidgetMapLoaded: boolean = false;
  sendFilterUsers: { [key: string]: boolean } = {};
  sendFilterWidgets: { [key: string]: boolean } = {};
  sendFilterTasks: number = 0;
  isSendingUserFilters: boolean = false;
  userFiltersSended: boolean = false;

  lastInterpretation = null;

  constructor(
    private _formBuilder: FormBuilder,
    private _backEndService: BackEndService,
    private _dataService: DataService,
    private _changeRef: ChangeDetectorRef,
    private _injector: Injector
  ) {
    this.formGroup = this._formBuilder.group({
      "filteruser": null,
      "filterbackgroundcolor": null,
      "filtercolor": null,
      "filterdesc": null,
      "filtericon": [null, Validators.required],
      "filterid": null,
      "filterseqnr": null,
      "filtertext": [null, Validators.required],
      "filtertype": [{ value: null, disabled: true }],
      "filterwidget": null,
      "filter": null,
      "filtercriteria": null,
      "filtersortattribute": null,
      "filtersortdsc": false
    });
    this.formSettedUp = true;
    this.filtersortdscControl.valueChanges.subscribe((value: boolean) => {
      if (value) this.sortDirection = "absteigend";
      else this.sortDirection = "aufsteigend";
    });
    this.filtercriteriaControl.valueChanges.subscribe(() => this.analyzeStatement());
    this.formGroup.valueChanges.subscribe(() => this.validateFormState());
    if (this.userFilter && !this.valuesInitiallySetted) this.setFormValues();
  }

  addCriteria(criteria: FilterTypeCriteria<number>) {
    this.currentSuggestions = [];
    if (criteria.suggestionsProvider) criteria.suggestionsProvider(criteria, this._injector).subscribe((result) => this.currentSuggestions = result);
    else {
      let input = "'" + criteria.value + " = '";
      let value = this.filtercriteriaControl.value;
      if (value) value += " & " + input;
      else value = input;
      this.filtercriteriaControl.setValue(value);
    }
  }

  addCriteriaValue(suggestion: CriteriaSuggestion<number>) {
    let input = "\"" + suggestion.criteria.value + " = " + suggestion.value + "\"";
    let value = this.filtercriteriaControl.value;
    if (value) value += " & " + input;
    else value = input;
    this.filtercriteriaControl.setValue(value);
  }

  analyzeStatement() { }

  deleteSavedFilter() {
    if (this.savedFilter) this._backEndService.delete("api/filter/" + this.savedFilter.filterid).subscribe(() => {
      this.savedFilter = null;
      this.userFilter.filter = null;
      this.userFilter.filteR1 = null;
      this.filterControl.setValue(null);
      this.analyzeStatement();
    });
  }

  initRights() {
    this._backEndService.rights.find(x => x.rulecode == "edit_datasets") ? this.rightsMap.isAdmin = true : this.rightsMap.isAdmin = false;
    this._changeRef.detectChanges();
  }

  ngOnDestroy() {
    for (let subscriptuion of this.subscriptions) subscriptuion.unsubscribe();
    this.subscriptions = [];
  }

  ngOnInit(): void {
    if (this.userFilter && this.formSettedUp && !this.valuesInitiallySetted) this.setFormValues();
    if (Array.isArray(this._backEndService.rights)) this.initRights();
    this.subscriptions.push(this._backEndService.rightsChanged.subscribe(() => this.initRights()));
  }

  saveFilterCondition(condition: FilterCondition) {
    this._backEndService.post("api/filtercondition", {
      attribute: condition.attribute,
      value: condition.value,
      filterid: condition.filterid,
      isnot: condition.isnot
    } as FilterCondition).subscribe((result: FilterCondition) => {
      condition.attribute = result.attribute;
      condition.filterid = result.filterid;
      condition.id = result.id;
      condition.isnot = result.isnot;
      condition.value = result.value;
    });
  }

  saveWidgetFilter(filter: Filter): Subject<any> {
    if (!filter.isNew) {
      console.warn("filter already saved!", filter);
      return;
    } else {
      console.log("saving: ", filter);
    }
    filter.isCurrentlySaving = true;
    let type = "AND";
    if (filter.type) type = filter.type;
    let subject = new Subject();
    this._backEndService.post("api/filter", {
      filterid: filter.filterid,
      filterparent: filter.filterparent,
      type: type
    }).subscribe((result: Filter) => {
      filter.filterid = result.filterid;
      filter.filterparent = result.filterparent;
      filter.type = result.type;
      filter.isNew = false;
      filter.isCurrentlySaving = false;
      for (let condition of filter.filtercondition) {
        condition.filterid = filter.filterid;
        this.saveFilterCondition(condition);
      }
      //if (Array.isArray(filter.children)) for (let child of filter.children) child.filterparent = filter.filterid;
      if (this.userFilter.filteR1 == filter) {
        this.savedFilter = filter;
        this.filterControl.setValue(filter.filterid);
        this.saveFilter();
      } else subject.next();
    });
    return subject;
  }

  saveCompiledFilters() {
    let newFilters = this.compiledFilters.filter(x => x.isNew);
    if (newFilters.length == 0) {
      if (this.savedFilter) {
        this._backEndService.get("api/filter/" + this.savedFilter.filterid).subscribe((filter: Filter) => {
          this.savedFilter = filter;
        });
      } else if (this.closeAnchor) this.closeAnchor.next();
    }
  }

  saveFilter() {
    let sortAttribute = null;
    if (this.filtersortattributeControl.value) sortAttribute = this.filtersortattributeControl.value.value;
    let o = {
      filterid: this.userFilter.filterid,
      filterbackgroundcolor: this.filterbackgroundcolorControl.value,
      filtercolor: this.filtercolorControl.value,
      filterdesc: this.filterdescControl.value,
      filtericon: this.filtericonControl.value,
      filterseqnr: this.filtersequencenumberControl.value,
      filtertext: this.filtertextControl.value,
      filteruser: this.userFilter.filteruser,
      filterwidget: this.userFilter.filterwidget,
      filter: this.filterControl.value,
      filtersortattribute: sortAttribute,
      filtersortdsc: this.filtersortdscControl.value
    } as WidgetUserFilter;
    if (this.filtertypeControl.value) o.filtertype = (this.filtertypeControl.value as FilterType<number>).value;
    let callback = (filter: WidgetUserFilter) => {
      this.userFilter.filterid = filter.filterid;
      this.userFilter.filterbackgroundcolor = filter.filterbackgroundcolor;
      this.userFilter.filtercolor = filter.filtercolor;
      this.userFilter.filterdesc = filter.filterdesc;
      this.userFilter.filtericon = filter.filtericon;
      this.userFilter.filterseqnr = filter.filterseqnr;
      this.userFilter.filtertext = filter.filtertext;
      this.userFilter.filtertype = filter.filtertype;
      this.userFilter.filteruser = filter.filteruser;
      this.userFilter.filterwidget = filter.filterwidget;
      this.userFilter.filtersortattribute = filter.filtersortattribute;
      this.userFilter.filtersortdsc = filter.filtersortdsc;
      this.saveCompiledFilters();
    };
    if (this.formHasChanges) {
      /*
      if (this.userFilter.isNew) this._backEndService.post("api/userfilter", o).subscribe((result: WidgetUserFilter) => {
        if (this.saveFinished) this.saveFinished.next(this.userFilter);
        callback(result);
      });
      else this._backEndService.put("api/userfilter", o).subscribe((result: WidgetUserFilter) => callback(result));
      */
    } else this.saveCompiledFilters();
  }

  sendFilters() {
    let taskResponse = new TaskResponse();
    this.subscriptions.push(taskResponse.onTaskSaved.subscribe((result) => {
      this.isSendingUserFilters = true;
    }));
    this.subscriptions.push(taskResponse.onAllTasksExecuted.subscribe(() => {
      this.isSendingUserFilters = false;
      this.userFiltersSended = true;
    }));
    let tasks: CreateUserFilterTask[] = [];
    for (let index in this.sendFilterWidgets) {
      if (this.sendFilterWidgets[index]) {

        let widget = this.currentUserWidgetMap
          .map(x => x.widgets)
          .reduce((acc, val) => acc.concat(val), [])
          .find(x => x.widgetid === parseInt(index)); // flat the widget[][]

        tasks.push({
          taskType: TaskType.CreateUserFilterTask,
          guid: Guid.newGuid(),
          filter: {
            filter: this.userFilter.filter,
            filterbackgroundcolor: this.userFilter.filterbackgroundcolor,
            filtercolor: this.userFilter.filtercolor,
            filterdesc: this.userFilter.filterdesc,
            filtericon: this.userFilter.filtericon,
            filterseqnr: 0,
            filtersortattribute: this.userFilter.filtersortattribute,
            filtersortdsc: this.userFilter.filtersortdsc,
            filtertext: this.userFilter.filtertext,
            filtertype: this.userFilter.filtertype,
            filteruser: widget.widgetuser,
            filterwidget: widget.widgetid,
            filterid: this.userFilter.filterid,
            filteR1: this.userFilter.filteR1
          } as WidgetUserFilter
        } as CreateUserFilterTask);
      }
    }
    this._dataService.saveTasks(taskResponse, tasks);
  }

  setFilterBackgroundColor(value: string) {
    this.filterbackgroundcolorControl.setValue(value);
  }

  setFilterColor(value: string) {
    this.filtercolorControl.setValue(value);
  }

  setFilterIcon(value: string) {
    this.filtericonControl.setValue(value);
  }

  setFormValues() {
    this.valuesInitiallySetted = true;
    this.filterControl.setValue(this.userFilter.filter);
    this.filtertextControl.setValue(this.userFilter.filtertext);
    this.filterdescControl.setValue(this.userFilter.filterdesc);
    this.filterbackgroundcolorControl.setValue(this.userFilter.filterbackgroundcolor);
    this.filtercolorControl.setValue(this.userFilter.filtercolor);
    this.filtericonControl.setValue(this.userFilter.filtericon);
    this.filtersortdscControl.setValue(this.userFilter.filtersortdsc);
    if (this.userFilter.filtertype) {
      this.currentFilterType = FilterTypes.find(x => x.value == this.userFilter.filtertype);
      this.filtertypeControl.setValue(this.currentFilterType);
    }
    if (this.userFilter.filtersortattribute && this.currentFilterType) {
      let type = this.currentFilterType.criteria.find(x => x.value == this.userFilter.filtersortattribute);
      this.filtersortattributeControl.setValue(type);
    }
    //this.filterControl.setValue(this.userFilter.)
  }

  setTermOperator(operator: string, term: Term) {
    switch (operator) {
      case 'AND':
        if(term.node){
          term.node.fn = "bitAnd";
          term.node.op = "&";
        }
        break;
      case 'OR':
        if(term.node){
          term.node.fn = "bitOr";
          term.node.op = "|";
        }
        break;
    }
    if(this.lastInterpretation){
      this.filtercriteriaControl.setValue(this.lastInterpretation.toString());
    }
  }

  setUserUFSendingState(user: User, event: MouseEvent) {
    event.stopPropagation();
    if (this.sendFilterUsers[user.id]) {
      this.sendFilterUsers[user.id] = false;
      for (let widget of this.currentUserWidgetMap.find(x => x.user === user).widgets) this.sendFilterWidgets[widget.widgetid.toString()] = false;
    } else {
      this.sendFilterUsers[user.id] = true;
      for (let widget of this.currentUserWidgetMap.find(x => x.user === user).widgets) this.sendFilterWidgets[widget.widgetid.toString()] = true;
    }
    this.validateSendFilterCount();
  }

  setWidgetUFSendingState(widget: Widget, event: MouseEvent) {
    event.stopPropagation();
    if (this.sendFilterWidgets[widget.widgetid.toString()]) this.sendFilterWidgets[widget.widgetid.toString()] = false;
    else this.sendFilterWidgets[widget.widgetid.toString()] = true;
    let entry = this.currentUserWidgetMap.find(x => x.user.id === widget.widgetuser);
    var found = false;
    for (let widget of entry.widgets) {
      if (!this.sendFilterWidgets[widget.widgetid.toString()]) {
        found = true;
        break;
      }
    }
    this.sendFilterUsers[entry.user.id] = !found;
    this.validateSendFilterCount();
  }

  tabChanged(event: MatTabChangeEvent) {
    switch (event.index) {
      case 2:
        if (!this.currentUserWidgetMapLoaded && !this.currentUserWidgetMapLoading) {
          this.currentUserWidgetMapLoading = true;
          this._backEndService.get("api/userfilter/shareFilter/" + this.userFilter.filterid).subscribe((userWidgetMap: UserWidgetMap[]) => {
            this.currentUserWidgetMap = userWidgetMap;
            this.currentUserWidgetMapLoaded = true;
            this.currentUserWidgetMapLoading = false;
            for (let entry of userWidgetMap) {
              this.sendFilterUsers[entry.user.id] = false;
              for (let widget of entry.widgets) this.sendFilterWidgets[widget.widgetid.toString()] = false;
            }
          });
        }
        break;
    }
  }

  validateSendFilterCount() {
    this.sendFilterTasks = 0;
    for (let index in this.sendFilterWidgets) {
      if (this.sendFilterWidgets[index]) {
        this.sendFilterTasks++;
      }
    }
  }

  validateFormState() {
    var detected: boolean = false;
    this.formIsValid = this.formGroup.valid;
    if (this.filterControl.value != this.userFilter.filter) {
      detected = true;
    }
    if (this.filtertextControl.value != this.userFilter.filtertext) {
      detected = true;
    }
    if (this.filterdescControl.value != this.userFilter.filterdesc) {
      detected = true;
    }
    if (this.filtercolorControl.value != this.userFilter.filtercolor) {
      detected = true;
    }
    if (this.filterbackgroundcolorControl.value != this.userFilter.filterbackgroundcolor) {
      detected = true;
    }
    if (this.filtericonControl.value != this.userFilter.filtericon) {
      detected = true;
    }
    if (this.filtersortdscControl.value != this.userFilter.filtersortdsc) {
      detected = true;
    }
    if (this.filtersortattributeControl.value) {
      let attribute = this.filtersortattributeControl.value.value;
      if (attribute && attribute != this.userFilter.filtersortattribute) detected = true;
    } else if (this.userFilter.filtersortattribute) detected = true;
    if (this.formIsValid) this.canModifyFilterConditions = true;
    else this.canModifyFilterConditions = false;
    this.formHasChanges = detected;
  }

  // CONTROLS
  get filterControl(): AbstractControl {
    return this.formGroup.controls["filter"];
  }
  get filterbackgroundcolorControl(): AbstractControl {
    return this.formGroup.controls["filterbackgroundcolor"];
  }
  get filtercolorControl(): AbstractControl {
    return this.formGroup.controls["filtercolor"];
  }
  get filterdescControl(): AbstractControl {
    return this.formGroup.controls["filterdesc"];
  }
  get filtericonControl(): AbstractControl {
    return this.formGroup.controls["filtericon"];
  }
  get filtersequencenumberControl(): AbstractControl {
    return this.formGroup.controls["filterseqnr"];
  }
  get filtersortattributeControl(): AbstractControl {
    return this.formGroup.controls["filtersortattribute"];
  }
  get filtertextControl(): AbstractControl {
    return this.formGroup.controls["filtertext"];
  }
  get filtertypeControl(): AbstractControl {
    return this.formGroup.controls["filtertype"];
  }
  get filtercriteriaControl(): AbstractControl {
    return this.formGroup.controls["filtercriteria"];
  }
  get filtersortdscControl(): AbstractControl {
    return this.formGroup.controls["filtersortdsc"];
  }
  get filterTypes(): FilterType<number>[] {
    return FilterTypes;
  }

}

export const FilterTypes: FilterType<number>[] = [
  {
    value: "Issue", title: "Vorgang", plural: "Vorgänge", criteria: [
      { value: "issuenumber", title: "Vorgangsnummer", suggestionsProvider: null },
      { value: "issuetitle", title: "Vorgangsbezeichnung", suggestionsProvider: null },
      {
        value: "issuestate", title: "Status", suggestionsProvider: (criteria, injector: Injector) => {
          return injector.get(IssueService).getAllIssueStatus(true).pipe(map((status: IssueStatus[]) => {
            return status.filter(x => x.isissuestate).map(x => {
              return { title: x.statename, criteria: criteria, value: x.stateid } as CriteriaSuggestion<any>;
            })
          }));
        }
      },
      {
        value: "issuepriority", title: "Priorität", suggestionsProvider: StaticFilterTypeCriteriaProviders.Priority
      },
      {
        value: "issuecategory", title: "Kategorie", suggestionsProvider: (criteria, injector: Injector) => {
          return injector.get(IssueService).getAllIssueCategories(true).pipe(map((categories: IssueCategory[]) => {
            return categories.filter(x => x.isissuecategory).map(x => {
              return { title: x.categoryname, criteria: criteria, value: x.categoryid } as CriteriaSuggestion<any>;
            })
          }));
        }
      },
      {
        value: "issueescalation", title: "Wunschdatum", suggestionsProvider: null
      },
      {
        value: "issuecreation", title: "Erstelldatum", suggestionsProvider: null
      },
      StaticFilterTpeCriteria.IssueUserConnectionAssigned,
      StaticFilterTpeCriteria.IssueFilesArchived
    ]
  },
  {
    value: "Change", title: "Change / RFC", plural: "Changes", criteria: [
      {
        value: "issuenumber", title: "Vorgangsnummer", suggestionsProvider: null
      },
      {
        value: "issuestate", title: "Status", suggestionsProvider: (criteria, injector: Injector) => {
          return injector.get(IssueService).getAllIssueStatus(true).pipe(map((status: IssueStatus[]) => {
            return status.filter(x => x.ischangestate).map(x => {
              return { title: x.statename, criteria: criteria, value: x.stateid } as CriteriaSuggestion<any>;
            })
          }));
        }
      },
      {
        value: "issuepriority", title: "Priorität", suggestionsProvider: StaticFilterTypeCriteriaProviders.Priority
      },
      {
        value: "issuecategory", title: "Kategorie", suggestionsProvider: (criteria, injector: Injector) => {
          return injector.get(IssueService).getAllIssueCategories(true).pipe(map((categories: IssueCategory[]) => {
            return categories.filter(x => x.ischangecategory).map(x => {
              return { title: x.categoryname, criteria: criteria, value: x.categoryid } as CriteriaSuggestion<any>;
            })
          }));
        }
      },
      {
        value: "issueescalation", title: "Wunschdatum", suggestionsProvider: null
      },
      {
        value: "issuecreation", title: "Erstelldatum", suggestionsProvider: null
      },
      StaticFilterTpeCriteria.IssueUserConnectionAssigned,
      StaticFilterTpeCriteria.IssueFilesArchived
    ]
  },
  {
    value: "Incident", title: "Ticket", plural: "Tickets", criteria: [
      {
        value: "issuenumber", title: "Vorgangsnummer", suggestionsProvider: null
      },
      {
        value: "issuestate", title: "Status", suggestionsProvider: (criteria, injector: Injector) => {
          return injector.get(IssueService).getAllIssueStatus(true).pipe(map((status: IssueStatus[]) => {
            return status.filter(x => x.isincidentstate).map(x => {
              return { title: x.statename, criteria: criteria, value: x.stateid } as CriteriaSuggestion<any>;
            })
          }));
        }
      },
      {
        value: "issuepriority", title: "Priorität", suggestionsProvider: StaticFilterTypeCriteriaProviders.Priority
      },
      {
        value: "issuecategory", title: "Kategorie", suggestionsProvider: (criteria, injector: Injector) => {
          return injector.get(IssueService).getAllIssueCategories(true).pipe(map((categories: IssueCategory[]) => {
            return categories.filter(x => x.isincidentcategory).map(x => {
              return { title: x.categoryname, criteria: criteria, value: x.categoryid } as CriteriaSuggestion<any>;
            })
          }));
        }
      },
      {
        value: "issueescalation", title: "Wunschdatum", suggestionsProvider: null
      },
      {
        value: "issuecreation", title: "Erstelldatum", suggestionsProvider: null
      },
      StaticFilterTpeCriteria.IssueUserConnectionAssigned,
      StaticFilterTpeCriteria.IssueFilesArchived
    ]
  },
  {
    value: "Project", title: "Projekt", plural: "Projekte", criteria: [
      {
        value: "issuenumber", title: "Vorgangsnummer", suggestionsProvider: null
      },
      {
        value: "issuestate", title: "Status", suggestionsProvider: (criteria, injector: Injector) => {
          return injector.get(IssueService).getAllIssueStatus(true).pipe(map((status: IssueStatus[]) => {
            return status.filter(x => x.isprojectstate).map(x => {
              return { title: x.statename, criteria: criteria, value: x.stateid } as CriteriaSuggestion<any>;
            })
          }));
        }
      },
      {
        value: "issuepriority", title: "Priorität", suggestionsProvider: StaticFilterTypeCriteriaProviders.Priority
      },
      {
        value: "issuecategory", title: "Kategorie", suggestionsProvider: (criteria, injector: Injector) => {
          return injector.get(IssueService).getAllIssueCategories(true).pipe(map((categories: IssueCategory[]) => {
            return categories.filter(x => x.isprojectcategory).map(x => {
              return { title: x.categoryname, criteria: criteria, value: x.categoryid } as CriteriaSuggestion<any>;
            })
          }));
        }
      },
      {
        value: "issueescalation", title: "Wunschdatum", suggestionsProvider: null
      },
      {
        value: "issuecreation", title: "Erstelldatum", suggestionsProvider: null
      },
      StaticFilterTpeCriteria.IssueUserConnectionAssigned,
      StaticFilterTpeCriteria.IssueFilesArchived
    ]
  },
  {
    value: "Contract", title: "Vertrag", plural: "Verträge", criteria: [
      { value: "contractnumber", title: "Vertragsnummer", suggestionsProvider: null },
      { value: "contractdesc", title: "Vertragsbezeichnung", suggestionsProvider: null },
      { value: "contractstate", title: "Vertragsstatus", suggestionsProvider: StaticFilterTypeCriteriaProviders.ContractState },
      { value: "contracttype", title: "Vertragstyp", suggestionsProvider: StaticFilterTypeCriteriaProviders.ContractType }
    ]
  },
  {
    value: "ContactPerson", title: "Ansprechpartner", plural: "Ansprechpartner", criteria: [
      { value: "contactperspublicid", title: "Nummer", suggestionsProvider: null },
      { value: "contactperssalutation", title: "Anrede", suggestionsProvider: null },
      { value: "contactpersfname", title: "Vorname", suggestionsProvider: null },
      { value: "contactperslname", title: "Nachname", suggestionsProvider: null }
    ]
  },
  {
    value: "Customer", title: "Kunde", plural: "Kunden", criteria: [
      { value: "customernumber", title: "Nummer", suggestionsProvider: null },
      { value: "customername", title: "Bezeichnung", suggestionsProvider: null },
      { value: "customeractive", title: "Aktiv", suggestionsProvider: (criteria, injector) => {
        return of([
          { value: "false", criteria: criteria, title: "Aktiv" } as CriteriaSuggestion<string>,
          { value: "true", criteria: criteria, title: "Inaktiv" } as CriteriaSuggestion<string>
        ]);
      }}
    ]
  },
  {
    value: "Location", title: "Standort", plural: "Standorte", criteria: [
      { value: "address.addressstreet", title: "Straße", suggestionsProvider: null },
      { value: "locationparent", title: "Hauptgebäude", suggestionsProvider: null }
    ]
  },
  {
    value: "Offer", title: "Angebot", plural: "Angebote", criteria: [
      { value: "offertitle", title: "Titel", suggestionsProvider: null }
    ]
  }
];

export class FilterType<T> {
  value: string;
  title: string;
  plural: string;
  criteria: FilterTypeCriteria<T>[]
}

export class FilterTypeCriteria<T> {
  value: string;
  title: string;
  suggestionsProvider: (criteria: FilterTypeCriteria<T>, injector: Injector) => Observable<CriteriaSuggestion<any>[]>;
}

export class CriteriaSuggestion<T> {
  value: T;
  title: string;
  criteria: FilterTypeCriteria<T>;
}

export class Term {
  isBeginParenthesis: boolean = false;
  isEndParenthesis: boolean = false;
  isOperator: boolean = false;
  isSymbol: boolean = false;
  isConstant: boolean = false;
  value: string;
  node?: any;
}

export class UserWidgetMap {
  user: User;
  widgets: Widget[];
}